import React, { useState } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { Upload } from 'lucide-react';

const WhyJoinUs = () => {
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    resume: null,
    jobRole: '',
    currentDesignation: '',
    currentOrganization: '',
    aboutYou: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const benefits = [
    { id: 1, icon: "🌍", title: "Global Impact", description: "Shape the future of AI education for 150,000+ students worldwide" },
    { id: 2, icon: "🧠", title: "Cutting-edge AI", description: "Work with the latest AI technologies and research methodologies" },
    { id: 3, icon: "👥", title: "Diverse Team", description: "Collaborate with experts from 25+ countries and varied backgrounds" },
    { id: 4, icon: "💡", title: "Innovation Freedom", description: "20% time for personal AI research projects and experimentation" },
    { id: 5, icon: "❤️", title: "Health & Wellness", description: "Comprehensive health coverage, mental health support, and wellness stipend" },
    { id: 6, icon: "🚀", title: "Career Growth", description: "Clear advancement paths, mentorship programs, and conference attendance" }
  ];

  // Modal handlers
  const handleShowModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      resume: null,
      jobRole: '',
      currentDesignation: '',
      currentOrganization: '',
      aboutYou: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    
    // Check for file input and validate size
    if (name === 'resume' && files && files[0]) {
      const file = files[0];
      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB in bytes

      if (file.size > MAX_FILE_SIZE) {
        alert('File size exceeds 10MB. Please upload a smaller file.');
        e.target.value = null; // Clear the invalid file from the input
        setFormData(prev => ({
          ...prev,
          resume: null
        }));
        return; // Prevent further processing
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  // Helper function to convert a file to a Base64 string
  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]); // Get only the Base64 part
    reader.onerror = error => reject(error);
  });

  // Updated handleSubmit to use Google Apps Script
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.resume || !formData.jobRole || !formData.aboutYou) {
        alert("Please fill out all required fields.");
        return;
    }
    setIsSubmitting(true);

    // --- This is your updated Google Apps Script Web App URL ---
    const GOOGLE_SCRIPT_ENDPOINT = 'https://script.google.com/macros/s/AKfycbxy5bdzpGH_iuiTniDfgmAq4FFNLRxnztUAm7jUzdWX-Xlvgje_oYHYHPnzU-6QXBi_/exec';

    try {
      // Convert the file to Base64
      const base64File = await fileToBase64(formData.resume);
      
      const submissionData = {
        ...formData,
        resume: {
          name: formData.resume.name,
          mimeType: formData.resume.type,
          data: base64File,
        },
      };

      const response = await fetch(GOOGLE_SCRIPT_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(submissionData),
        headers: {
          'Content-Type': 'text/plain;charset=utf-8', // Required for Apps Script
        },
      });

      const result = await response.json();

      if (result.status === 'success') {
        alert('Thank you for your application! We will review your submission and contact you within 1-2 weeks if your profile matches our current openings.');
        handleCloseModal();
      } else {
        throw new Error(result.message || 'An unknown error occurred.');
      }
    } catch (error) {
      console.error('Submission failed:', error);
      alert(`An error occurred while submitting your application: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Base styles for the component (unchanged)
  const styles = {
    container: {
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    benefitsSection: {
      background: '#0a0a0a',
      padding: '80px 20px',
      minHeight: '70vh'
    },
    sectionTitle: {
      fontSize: '48px',
      fontWeight: '700',
      color: '#ffffff',
      textAlign: 'center',
      margin: '0 0 60px 0',
      textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)'
    },
    benefitsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '30px',
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 20px'
    },
    benefitCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '20px',
      padding: '40px 30px',
      textAlign: 'center',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
    },
    iconContainer: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 25px auto',
      boxShadow: '0 8px 25px rgba(139, 92, 246, 0.3)'
    },
    icon: {
      fontSize: '32px',
      filter: 'brightness(1.2)'
    },
    benefitTitle: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#ffffff',
      margin: '0 0 15px 0'
    },
    benefitDescription: {
      fontSize: '16px',
      color: 'rgba(255, 255, 255, 0.8)',
      lineHeight: '1.6',
      margin: '0'
    },
    ctaSection: {
      background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 50%, #3b82f6 100%)',
      padding: '80px 20px',
      minHeight: '30vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    ctaContent: {
      textAlign: 'center',
      maxWidth: '800px',
      margin: '0 auto'
    },
    ctaTitle: {
      fontSize: '42px',
      fontWeight: '700',
      color: '#ffffff',
      margin: '0 0 25px 0',
      textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
    },
    ctaDescription: {
      fontSize: '18px',
      color: 'rgba(255, 255, 255, 0.9)',
      lineHeight: '1.7',
      margin: '0 auto 40px auto',
      maxWidth: '600px',
    },
    ctaButtons: {
      display: 'flex',
      gap: '20px',
      justifyContent: 'center',
      flexWrap: 'wrap'
    },
    primaryButton: {
      background: 'rgba(0, 0, 0, 0.3)',
      color: '#ffffff',
      border: '2px solid rgba(255, 255, 255, 0.3)',
      borderRadius: '12px',
      padding: '15px 30px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      backdropFilter: 'blur(10px)',
      boxShadow: '0 4px 15px rgba(255, 255, 255, 0.1)'
    },
    secondaryButton: {
      background: 'rgba(0, 0, 0, 0.2)',
      color: '#ffffff',
      border: '2px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '12px',
      padding: '15px 30px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      backdropFilter: 'blur(10px)',
      boxShadow: '0 4px 15px rgba(255, 255, 255, 0.1)'
    },
    buttonIcon: {
      fontSize: '18px'
    }
  };

  return (
    <>
      <div style={styles.container}>
        <style>
          {`
            /* --- Hover Effects --- */
            [data-component="benefit-card"]:hover {
              transform: translateY(-8px) scale(1.03);
              background: rgba(139, 92, 246, 0.15);
              box-shadow: 0 20px 40px rgba(139, 92, 246, 0.2);
              border-color: rgba(139, 92, 246, 0.3);
            }
            [data-component="primary-button"]:hover,
            [data-component="secondary-button"]:hover {
              transform: scale(1.05);
              box-shadow: 0 8px 25px rgba(255, 255, 255, 0.2);
            }

            /* --- Responsive Design --- */
            @media (max-width: 768px) {
              [data-component="benefits-section"],
              [data-component="cta-section"] {
                padding: 60px 15px !important;
              }
              [data-component="section-title"],
              [data-component="cta-title"] {
                font-size: 36px !important;
              }
              [data-component="benefits-grid"] {
                grid-template-columns: 1fr !important;
                padding: 0 !important;
              }
              [data-component="cta-buttons"] {
                flex-direction: column !important;
              }
              [data-component="primary-button"],
              [data-component="secondary-button"] {
                width: 100%;
                justify-content: center;
              }
            }
          `}
        </style>

        <div style={styles.benefitsSection} data-component="benefits-section">
          <h2 style={styles.sectionTitle} data-component="section-title">Why Join Us?</h2>
          <div style={styles.benefitsGrid} data-component="benefits-grid">
            {benefits.map(benefit => (
              <div key={benefit.id} style={styles.benefitCard} data-component="benefit-card">
                <div style={styles.iconContainer}>
                  <span style={styles.icon}>{benefit.icon}</span>
                </div>
                <h3 style={styles.benefitTitle}>{benefit.title}</h3>
                <p style={styles.benefitDescription}>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.ctaSection} data-component="cta-section">
          <div style={styles.ctaContent}>
            <h2 style={styles.ctaTitle} data-component="cta-title">Ready to Join the AI Revolution?</h2>
            <p style={styles.ctaDescription}>
              Be part of a global community that's transforming how the world learns AI. Your ideas, passion, and 
              expertise can help democratize AI education for millions.
            </p>
            <div style={styles.ctaButtons} data-component="cta-buttons">
              <button 
                style={styles.primaryButton} 
                data-component="primary-button"
                onClick={handleShowModal}
              >
                <span style={styles.buttonIcon}>⬆️</span>
                Submit Your Resume
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bootstrap Modal for Resume Submission */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton style={{ 
          borderBottom: '1px solid #e2e8f0', 
          padding: '1.5rem',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
        }}>
          <Modal.Title style={{ 
            fontSize: '1.5rem', 
            fontWeight: '700', 
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            💼 Submit Your Resume
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={{ padding: '2rem', background: '#ffffff' }}>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Full Name *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your full name"
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Email Address *
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your.email@example.com"
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Phone Number
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 123-4567"
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Resume/CV * (PDF, DOC, DOCX - Max 10MB)
                  </Form.Label>
                  <Form.Control
                    type="file"
                    name="resume"
                    onChange={handleInputChange}
                    accept=".pdf,.doc,.docx"
                    required
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                  {formData.resume && (
                    <div style={{ 
                      marginTop: '0.5rem', 
                      padding: '0.5rem', 
                      background: '#f8fafc', 
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      color: '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <Upload size={14} />
                      {formData.resume.name} ({(formData.resume.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Which Job Role Interests You? *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="jobRole"
                    value={formData.jobRole}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., AI Researcher, Software Engineer"
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Current Designation *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="currentDesignation"
                    value={formData.currentDesignation}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Student, Senior Developer"
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Current Organization *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="currentOrganization"
                    value={formData.currentOrganization}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your current company/organization"
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                Brief About You *
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                name="aboutYou"
                value={formData.aboutYou}
                onChange={handleInputChange}
                required
                placeholder="Tell us about yourself, your experience, skills, achievements, and why you're interested in joining our AI education mission..."
                style={{ 
                  borderRadius: '8px',
                  border: '2px solid #e2e8f0',
                  padding: '0.75rem',
                  fontSize: '0.875rem',
                  transition: 'border-color 0.2s ease',
                  resize: 'vertical'
                }}
                onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </Form.Group>

            <div style={{
              background: 'linear-gradient(135deg, #ede9fe 0%, #f3e8ff 100%)',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              border: '1px solid #8b5cf6'
            }}>
              <p style={{ 
                margin: 0, 
                fontSize: '0.8rem', 
                color: '#5b21b6',
                lineHeight: '1.4'
              }}>
                🚀 <strong>What's Next:</strong> Our HR team will review your application within 1-2 days. If your profile matches our current openings, we'll reach out to schedule an initial conversation. We're excited to learn more about you!
              </p>
            </div>

            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              justifyContent: 'flex-end',
              paddingTop: '1rem',
              borderTop: '1px solid #e2e8f0'
            }}>
              <Button 
                variant="outline-secondary" 
                onClick={handleCloseModal}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontWeight: '600',
                  border: '2px solid #e2e8f0',
                  color: '#f80000ff'
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '8px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
                  border: 'none',
                  boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)'
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Resume'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default WhyJoinUs;
